<template>
  <div style="position: relative">
    <v-card flat>
      <div id="controlsContainer">
        <div
          class="card-title table-controls px-3 d-flex align-center justify-space-between"
        >
          <div>
            <!-- CRUD buttons -->
            <!-- dialog for creating item -->
          </div>
          <!-- center side -->
          <div>
            <!-- Search by fields -->
            <!-- Search in table -->
            <v-container id="filterContainer">
              <v-row>
                <v-col sm="4" lg="4" cols="12">
                  <v-dialog
                    v-model="modalFilterDateFrom"
                    :close-on-content-click="false"
                    persistent
                    width="290px"
                    @keydown.esc="modalFilterDateFrom = false"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.dateFromFormatted"
                        :label="$t('purchaseData.dateFrom')"
                        append-icon="far fa-calendar-alt"
                        readonly
                        clearable
                        v-on="on"
                        @click:clear="filter.dateFrom = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.dateFrom"
                      scrollable
                      @input="modalFilterDateFrom = false"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterDateFrom = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col sm="4" lg="4" cols="12">
                  <v-dialog
                    v-model="modalFilterDateTo"
                    :close-on-content-click="false"
                    persistent
                    width="290px"
                    @keydown.esc="modalFilterDateTo = false"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.dateToFormatted"
                        :label="$t('purchaseData.dateTo')"
                        append-icon="far fa-calendar-alt"
                        readonly
                        clearable
                        v-on="on"
                        @click:clear="filter.dateTo = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.dateTo"
                      scrollable
                      @input="modalFilterDateTo = false"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterDateTo = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col sm="4" lg="4" cols="12">
                  <v-select
                    v-model="filter.billed"
                    :items="billedItemsValues"
                    :label="$t('purchaseData.datatable.billed')"
                  ></v-select>
                </v-col>
              </v-row>
            </v-container>
            <!-- clear filters -->
          </div>
          <!-- right side -->
          <div>
            <!-- Refresh table -->
            <crud-button
              large
              color="blue"
              @clicked="refreshItemsView()"
              icon="fa-sync-alt"
              :tooltip="$t('global.crud.buttons.refreshTable')"
            />
            <ExportExcel
              url="/purchaseData"
              :filter="this.filter"
              :filterExport="filterExport"
            />
          </div>
        </div>
      </div>
      <div id="dataContainer">
        <!-- Table -->
        <v-data-table
          :headers="headers"
          :items="items"
          item-key="code"
          :options.sync="options"
          :page.sync="pagination.page"
          :server-items-length="pagination.totalItems"
          :no-results-text="$t('global.datatable.noMatchingResults')"
          :no-data-text="$t('global.datatable.noDataAvailable')"
          :footer-props="{
            'items-per-page-options': rowsPerPageItems,
            'show-first-last-page': true,
            'show-current-page': true
          }"
          :loading="loading"
          light
          multi-sort
          dense
        >
          <template v-slot:body="{ items }">
            <tbody v-if="items.length > 0">
              <tr v-for="item in items" :key="item.code">
                <td>{{ item.order }}</td>
                <td>{{ item.expedition.token }}</td>
                <td>{{ item.expedition.deliveryOrderId }}</td>
                <td v-nl2br>
                  {{
                    item.expedition.additionalServices
                      .filter(x => x.purchased)
                      .map(x => x.productTypeCode + "-" + x.name)
                      .join(",\n\r")
                  }}
                </td>
                <td>
                  {{
                    transformDate(
                      item.creationDate,
                      $t("global.dateHourFormat")
                    )
                  }}
                </td>
                <td>{{ item.expedition.senderCode }}</td>
                <td>{{ item.expedition.senderName }}</td>
                <td>{{ item.buyersName }}</td>
                <td>{{ item.buyersAddress }}</td>
                <td>{{ item.buyersPostalCode }}</td>
                <td>{{ item.buyersCity }}</td>
                <td>{{ item.buyersTelephone }}</td>
                <td>
                  <i18n-n
                    tag="span"
                    class="item__value"
                    :value="item.amount"
                  ></i18n-n>
                </td>
                <td>
                  {{
                    item.resultCode === "0000"
                      ? $t("global.yes")
                      : $t("global.no")
                  }}
                </td>
                <td>{{ item.resultCode }}</td>
                <td>
                  <v-switch
                    @change="updateBilled(item)"
                    v-model="item.billed"
                  ></v-switch>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td :colspan="headers.length" style="text-align: center">
                  {{ $t("global.datatable.noMatchingResults") }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
    </v-card>
  </div>
</template>

<script>
import CrudButton from "@/components/Button.vue";
import localhttp from "../js/https-commons";
import { transformDate } from "@/js/utils.js";
import ExportExcel from "@/components/ExportExcel.vue";

const CONTEXT = "/purchaseData";
const ENTITY_NAME = "purchaseData.";

export default {
  components: {
    CrudButton,
    ExportExcel
  },
  name: "PurchaseData",
  data: () => ({
    loading: false,
    searchTableWaiting: false,
    modalFilterDateFrom: false,
    modalFilterDateTo: false,
    modalFilterBilled: null,
    item: {},
    items: [],
    filter: {
      allFields: "",
      dateFrom: null,
      dateFromFormatted: "",
      dateTo: null,
      dateToFormatted: "",
      details: true,
      deletedItems: "ALL"
    },
    options: {},
    pagination: {
      totalItems: 0,
      page: 1
    },
    rowsPerPageItems: [10, 20, 30, 40]
  }),
  directives: {
    nl2br: {
      inserted(el) {
        el.innerHTML = el.textContent.replace(/\n/g, "<br />");
      }
    }
  },
  computed: {
    billedItemsValues: function() {
      return [
        { text: this.$t("global.status.all"), value: null },
        { text: this.$t("global.yes"), value: true },
        { text: this.$t("global.no"), value: false }
      ];
    },
    headers() {
      return [
        {
          text: this.$t("purchaseData.datatable.order"),
          value: ENTITY_NAME + "order"
        },
        {
          text: this.$t("purchaseData.datatable.expedition"),
          sortable: false
        },
        {
          text: this.$t("purchaseData.datatable.shipment"),
          sortable: false
        },
        {
          text: this.$t("purchaseData.datatable.additionalService"),
          sortable: false
        },
        {
          text: this.$t("purchaseData.datatable.purchaseDate"),
          value: ENTITY_NAME + "creationDate"
        },
        {
          text: this.$t("purchaseData.datatable.senderCode"),
          value: "expedition.senderCode"
        },
        {
          text: this.$t("purchaseData.datatable.senderName"),
          value: "expedition.senderName"
        },
        {
          text: this.$t("purchaseData.datatable.buyersName"),
          value: ENTITY_NAME + "buyersName"
        },
        {
          text: this.$t("purchaseData.datatable.buyersAddress"),
          value: ENTITY_NAME + "buyersAddress"
        },
        {
          text: this.$t("purchaseData.datatable.buyersPostalCode"),
          value: ENTITY_NAME + "buyersPostalCode"
        },
        {
          text: this.$t("purchaseData.datatable.buyersCity"),
          value: ENTITY_NAME + "buyersCity"
        },
        {
          text: this.$t("purchaseData.datatable.buyersTelephone"),
          value: ENTITY_NAME + "buyersTelephone"
        },
        {
          text: this.$t("purchaseData.datatable.amount"),
          value: ENTITY_NAME + "amount"
        },
        {
          text: this.$t("purchaseData.datatable.charged"),
          value: ENTITY_NAME + "resultCode"
        },
        {
          text: this.$t("purchaseData.datatable.resultCode"),
          value: ENTITY_NAME + "resultCode"
        },
        {
          text: this.$t("purchaseData.datatable.billed"),
          value: ENTITY_NAME + "billed"
        }
      ];
    }
  },
  watch: {
    options: {
      handler() {
        this.search();
      },
      deep: true
    },
    "filter.dateFrom": function() {
      this.filter.dateFromFormatted = this.transformDate(
        this.filter.dateFrom,
        this.$t("global.dateFormat")
      );
    },
    "filter.dateTo": function() {
      this.filter.dateToFormatted = this.transformDate(
        this.filter.dateTo,
        this.$t("global.dateFormat")
      );
    }
  },
  methods: {
    search() {
      console.debug("Search elements");

      // Items have already been requested
      if (this.loading) return;

      this.loading = true;

      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.filter.sortBy = sortBy;
      this.filter.sortDesc = sortDesc;
      this.filter.page = page;
      this.filter.itemsPerPage = itemsPerPage;

      localhttp
        .post(CONTEXT + "/allPagination", this.filter)
        .then(response => {
          this.pagination.totalItems = response.data.totalElements;
          this.items = response.data.content;
          this.items.map(x => (x.billed = x.billed == true));
          this.loading = false;
        })
        .catch(e => {
          console.log(e);
          this.loading = false;
          this.$showError();
        });
    },
    async updateBilled(item) {
      console.debug("Update billed");
      localhttp
        .put(CONTEXT + "/updateBilled/" + item.code + "/" + item.billed)
        .then(response => {
          console.log(response.data);
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
          item.billed = !item.billed;
        });
    },
    refreshItemsView() {
      console.debug("Refresh table");
      this.search();
    },
    filterExport(jsonData) {
      var vm = this;
      for (var index in jsonData) {
        let v = jsonData[index];
        let jsonReturn = {};
        jsonReturn.order = v.order;
        jsonReturn.expedition = v.expedition.token;
        jsonReturn.deliveryOrderId = v.expedition.deliveryOrderId;
        jsonReturn.additionalServices = v.expedition.additionalServices
          .filter(x => x.purchased)
          .map(x => x.productTypeCode + "-" + x.name)
          .join(",\n\r");
        jsonReturn.creationDate = transformDate(
          v.creationDate,
          vm.$t("global.dateHourFormat")
        );
        jsonReturn.clientCode = v.expedition.senderCode;
        jsonReturn.clientName = v.expedition.senderName;
        jsonReturn.buyersName = v.buyersName;
        jsonReturn.buyersAddress = v.buyersAddress;
        jsonReturn.buyersPostalCode = v.buyersPostalCode;
        jsonReturn.buyersCity = v.buyersCity;
        jsonReturn.buyersCountry = v.buyersCountry;
        jsonReturn.buyersTelephone = v.buyersTelephone;
        jsonReturn.buyersMail = v.buyersMail;
        jsonReturn.cif = v.buyersIdentity;
        jsonReturn.amount = v.amount;
        jsonReturn.currency = v.currency;
        jsonReturn.operationError = v.resultOperation;
        jsonReturn.charged =
          v.resultCode === "0000" ? vm.$t("global.yes") : vm.$t("global.no");
        jsonReturn.resultCode = v.resultCode;
        jsonReturn.resutError = v.resutError;
        jsonReturn.billed = v.billed;
        jsonData[index] = jsonReturn;
      }
    },
    transformDate
  }
};
</script>
